import type { DomainEventDescriptor, DomainEventHandler, Unsubscribe } from './types';

/**
 * The in-process subscriber registry, keyed by descriptor `type`.
 *
 * Deliberately module-scope (not injected): a domain event is an in-process
 * notification between layers of one running app, and both sides resolve the
 * same module instance through the workspace's single `@heidi/runtime` copy —
 * the same property the feature-flags door depends on.
 */
const subscribers = new Map<string, Set<DomainEventHandler<never>>>();

/**
 * Announce that something happened in the emitting domain.
 *
 * **Owner-only.** Only the domain that owns the event's noun may emit its
 * descriptor, and emission accompanies that domain's own mutation. A foreign
 * domain that produces an equivalent backend effect (desktop's bulk sync
 * creating sessions, say) emits **its own** event — impersonating the owner's
 * makes the owner's invariants unverifiable at the emit site.
 *
 * **Loss-tolerant.** This is a notification, never a hidden command: a
 * subscriber refreshes or invalidates its own state, and correctness must not
 * depend on delivery. Nothing is buffered, so a handler registered after the
 * emit does not see it.
 *
 * Delivery is synchronous and ordered by subscription. A handler that throws
 * cannot break the emitting mutation or starve the handlers after it — the
 * error is re-thrown on a microtask instead, so it still reaches the global
 * error handler (and Datadog RUM) rather than being swallowed.
 */
export function emitDomainEvent<TPayload>(
  descriptor: DomainEventDescriptor<TPayload>,
  payload: TPayload
): void {
  const handlers = subscribers.get(descriptor.type);
  if (handlers === undefined) return;
  // Snapshot + liveness check, the two halves of re-entrancy safety (same
  // shape as DOM event dispatch): the snapshot means a handler that subscribes
  // during delivery waits for the NEXT emit rather than seeing this one, and
  // re-checking membership means a handler torn down mid-delivery is not
  // called after its unsubscribe.
  for (const handler of [...handlers] as Array<DomainEventHandler<TPayload>>) {
    if (!handlers.has(handler as DomainEventHandler<never>)) continue;
    try {
      handler(payload);
    } catch (error) {
      queueMicrotask(() => {
        throw error;
      });
    }
  }
}

/**
 * React to another domain's event.
 *
 * Subscribing imports the descriptor, so the subscription **is** a declared
 * contracts edge and must follow the domain DAG (subscriber above emitter —
 * `tasks → session` is declared, so `tasks/data` may subscribe to session's
 * events). A reaction that would run against the DAG (patients reacting to a
 * session event, where `session → patients` is the declared direction) belongs
 * in an app-side composition/integration module calling the lower domain's own
 * exported invalidator — not in a `data/` subscription.
 *
 * Returns an idempotent unsubscribe; call it on teardown.
 */
export function subscribeDomainEvent<TPayload>(
  descriptor: DomainEventDescriptor<TPayload>,
  handler: DomainEventHandler<TPayload>
): Unsubscribe {
  const handlers =
    subscribers.get(descriptor.type) ?? new Set<DomainEventHandler<never>>();
  subscribers.set(descriptor.type, handlers);
  handlers.add(handler as DomainEventHandler<never>);

  let active = true;
  return () => {
    if (!active) return;
    active = false;
    handlers.delete(handler as DomainEventHandler<never>);
    if (handlers.size === 0) subscribers.delete(descriptor.type);
  };
}

/** Test-only: clears every subscription so suites can't leak into each other. */
export function __resetDomainEventsForTests(): void {
  subscribers.clear();
}
